
import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, Star, ArrowRight, Filter, FileText, Briefcase, TrendingUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";
import { JobMatchingSearch } from "@/components/job-matching/JobMatchingSearch";
import { JobMatchingResults } from "@/components/job-matching/JobMatchingResults";
import { PremiumFeatures } from "@/components/job-matching/PremiumFeatures";
import { useJobMatching, JobFilters } from "@/components/job-matching/useJobMatching";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";

const AIJobMatching = () => {
 const { toast } = useToast();
 const { isPremium, user } = useUserStore();
 const { 
   isAnalyzing, 
   jobMatchResults, 
   handleFindMatches,
   savedJobs,
   toggleSaveJob,
   activeFilters: jobMatchingActiveFilters,
   updateFilters,
   isSaved,
   clearResults,
   searchHistory,
   matchQuality,
   jobCategories
 } = useJobMatching();

 const [activeTab, setActiveTab] = useState("search");
 const [showInsights, setShowInsights] = useState(false);

 useEffect(() => {
   // Analytics tracking for page view
   if (typeof window !== 'undefined') {
     // Track page view
   }
 }, []);

 const handleTabChange = (value) => {
   setActiveTab(value);
   // Track tab change
 };

 const renderJobInsights = () => {
   if (!jobMatchResults.length) return null;
   
   return (
     <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 shadow-sm mb-6">
       <CardContent className="p-4">
         <div className="flex items-center justify-between mb-3">
           <h3 className="text-lg font-semibold text-blue-800">Job Market Insights</h3>
           <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">AI Generated</Badge>
         </div>
         
         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
           <div className="flex flex-col p-3 bg-white rounded-lg border border-blue-100">
             <div className="flex items-center mb-2">
               <TrendingUp className="w-4 h-4 text-blue-500 mr-2" />
               <span className="text-sm font-medium text-blue-700">Match Quality</span>
             </div>
             <Progress value={matchQuality || 65} className="h-2 mb-1" />
             <span className="text-xs text-blue-600">{matchQuality || 65}% alignment with your profile</span>
           </div>
           
           <div className="flex flex-col p-3 bg-white rounded-lg border border-blue-100">
             <div className="flex items-center mb-2">
               <Briefcase className="w-4 h-4 text-blue-500 mr-2" />
               <span className="text-sm font-medium text-blue-700">Top Categories</span>
             </div>
             <div className="flex flex-wrap gap-1">
               {(jobCategories || ['Software Development', 'Engineering', 'Product']).map(category => (
                 <Badge key={category} variant="secondary" className="bg-blue-50 text-blue-700 text-xs">{category}</Badge>
               ))}
             </div>
           </div>
           
           <div className="flex flex-col p-3 bg-white rounded-lg border border-blue-100">
             <div className="flex items-center mb-2">
               <FileText className="w-4 h-4 text-blue-500 mr-2" />
               <span className="text-sm font-medium text-blue-700">Resume Optimization</span>
             </div>
             <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 text-xs p-0 h-auto" 
               onClick={() => isPremium ? setShowInsights(true) : toast({
                 title: "Premium Feature",
                 description: "Upgrade to Premium to access resume optimization insights.",
               })}>
               {isPremium ? "View 8 optimization tips" : "Upgrade to view insights"}
             </Button>
           </div>
         </div>
         
         {isPremium && (
           <p className="text-sm text-blue-600 italic">
             Our AI has analyzed {jobMatchResults.length} job postings and found that highlighting your 
             {' '}<span className="font-medium">project management</span> and 
             {' '}<span className="font-medium">data analysis</span> skills could increase your match rate by 18%.
           </p>
         )}
       </CardContent>
     </Card>
   );
 };

 return (
   <div className="container mx-auto px-4 py-8 animate-fade-in">
     <div className="flex items-center justify-between mb-8">
       <div>
         <h1 className="text-3xl font-bold text-gradient">AI Job Matching</h1>
         <p className="text-gray-500 mt-1">Find jobs that match your skills and experience using AI</p>
       </div>
       {isPremium ? (
         <Badge variant="outline" className="bg-gradient-to-r from-amber-200 to-amber-400 text-amber-900 border-amber-500 px-3 py-1">
           <Star className="w-4 h-4 mr-1 fill-amber-500 text-amber-500" /> Premium
         </Badge>
       ) : (
         <Button variant="outline" size="sm" className="gap-1" onClick={() => toast({
           title: "Premium Feature",
           description: "Upgrade to Premium for enhanced AI job matching and higher match scores.",
         })}>
           <Sparkles className="w-4 h-4" /> Upgrade to Premium
         </Button>
       )}
     </div>
     
     <Tabs defaultValue="search" className="mb-8" onValueChange={handleTabChange}>
       <TabsList className="bg-gray-100 p-1">
         <TabsTrigger value="search" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
           Search & Match
         </TabsTrigger>
         <TabsTrigger value="saved" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
           Saved Jobs {savedJobs.length > 0 && (
             <Badge variant="secondary" className="ml-1 bg-blue-100 text-blue-700 text-xs">{savedJobs.length}</Badge>
           )}
         </TabsTrigger>
         {isPremium && (
           <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
             Search History
           </TabsTrigger>
         )}
       </TabsList>
       
       <TabsContent value="search" className="mt-6">
         <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
           <div className="lg:col-span-2">
             <JobMatchingSearch 
               onSearch={handleFindMatches} 
               isAnalyzing={isAnalyzing} 
               onClear={clearResults}
               activeFilters={jobMatchingActiveFilters}
             />
           </div>
           <div className="lg:col-span-1">
             <PremiumFeatures />
           </div>
         </div>
         
         {jobMatchResults.length > 0 && renderJobInsights()}
         
         <div className="flex items-center justify-between mb-4">
           <h2 className="text-xl font-semibold text-gray-800">
             {isAnalyzing ? "Analyzing Jobs..." : `Matched Jobs (${jobMatchResults.length})`}
           </h2>
           
           {jobMatchResults.length > 0 && (
             <div className="flex gap-2">
               <Button 
                 variant="outline" 
                 size="sm" 
                 className="gap-1"
                 onClick={() => {
                   // Open filter modal or expand filter section
                   toast({
                     title: "Filters Applied",
                     description: "Your filters have been updated.",
                   });
                 }}
               >
                 <Filter className="h-4 w-4" /> Filter
               </Button>
               
               <Button 
                 variant="ghost" 
                 size="sm" 
                 className="text-gray-500"
                 onClick={clearResults}
               >
                 Clear Results
               </Button>
             </div>
           )}
         </div>
         
         <JobMatchingResults 
           jobs={jobMatchResults} 
           isPremium={isPremium} 
           isAnalyzing={isAnalyzing}
           savedJobs={savedJobs}
           toggleSaveJob={toggleSaveJob}
           isSaved={isSaved}
           updateFilters={updateFilters}
         />
       </TabsContent>
       
       <TabsContent value="saved" className="mt-6">
         {savedJobs.length > 0 ? (
           <>
             <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-100">
               <h3 className="text-md font-medium text-blue-700 mb-1">You have {savedJobs.length} saved jobs</h3>
               <p className="text-sm text-blue-600">
                 {isPremium 
                   ? "Premium feature: We'll notify you of application deadlines and job status changes." 
                   : "Upgrade to Premium to get notified of application deadlines and job status changes."}
               </p>
             </div>
             
             <JobMatchingResults 
               jobs={jobMatchResults.filter(job => isSaved(job.id))}
               isPremium={isPremium}
               isAnalyzing={false}
               savedJobs={savedJobs}
               toggleSaveJob={toggleSaveJob}
               isSaved={isSaved}
             />
           </>
         ) : (
           <div className="bg-gray-50 rounded-lg border border-dashed p-8 text-center">
             <h3 className="text-xl font-medium text-gray-700 mb-2">No Saved Jobs</h3>
             <p className="text-gray-500 max-w-md mx-auto mb-6">
               You haven't saved any jobs yet. When you find jobs you're interested in, save them to revisit later.
             </p>
             <Button variant="default" className="gap-2" onClick={() => setActiveTab("search")}>
               Find Jobs <ArrowRight className="h-4 w-4" />
             </Button>
           </div>
         )}
       </TabsContent>
       
       {isPremium && (
         <TabsContent value="history" className="mt-6">
           <Card>
             <CardContent className="p-6">
               <h3 className="text-lg font-medium text-gray-800 mb-4">Search History</h3>
               {(searchHistory?.length > 0) ? (
                 <ScrollArea className="h-[300px] rounded-md border p-4">
                   <div className="space-y-4">
                     {(searchHistory || []).map((search, idx) => (
                       <div key={idx} className="border-b pb-3 last:border-0">
                         <div className="flex justify-between items-start">
                           <div>
                             <p className="font-medium">{search.query || "React Developer"}</p>
                             <p className="text-sm text-gray-500">{search.date || "Feb 28, 2025"} • {search.results || 42} results</p>
                           </div>
                           <Button variant="ghost" size="sm" onClick={() => handleFindMatches(search.query)}>
                             Search Again
                           </Button>
                         </div>
                       </div>
                     ))}
                   </div>
                 </ScrollArea>
               ) : (
                 <div className="text-center py-8 text-gray-500">
                   <p>Your search history will appear here</p>
                 </div>
               )}
             </CardContent>
           </Card>
         </TabsContent>
       )}
     </Tabs>
   </div>
 );
};

export default AIJobMatching;
